import { Button } from '@accure/ui';

import { Tooltip } from '@/components/ui/tooltip';
import { AlertType } from '@/entities/alert-type';
import { TaskCardType } from '@/task-manager/entities/task-card-type';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import { TaskStatus } from '@/task-manager/entities/task-status';
import { isStale } from '@/task-manager/utils/safety-score';

export type SafetyScoreResetButtonProps = {
  task: TaskDetails;
  onClick: () => void;
};

function shouldShowSafetyResetButton(task: TaskDetails): boolean {
  if (!task.alert?.type) return false;

  const allowedTypes = [
    AlertType.SAFETY,
    AlertType.PERFORMANCE,
    AlertType.IMBALANCE,
    AlertType.SOC_OFFSET,
  ];

  return allowedTypes.includes(task.alert.type);
}

export function SafetyScoreResetButton({
  task,
  onClick,
}: SafetyScoreResetButtonProps) {
  const isButtonDisabled = !task.canResetScore || isStale(task);

  if (
    task.cardType !== TaskCardType.ALERT ||
    task.status !== TaskStatus.DONE ||
    !shouldShowSafetyResetButton(task)
  ) {
    return null;
  }

  return (
    <Tooltip
      hide={!isButtonDisabled}
      content={
        isStale(task)
          ? 'This task is stale and cannot trigger a reset request'
          : 'A request has already been submitted'
      }
    >
      <Button
        className="py-2"
        variant="outline"
        onClick={onClick}
        disabled={isButtonDisabled}
        data-testid="safety-score-reset-button"
      >
        Request safety score reset&hellip;
      </Button>
    </Tooltip>
  );
}
